import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { AI_PROGRESS_STATUSES, AILanguage, AIStreamEvent, AIConversationHistory, AIConversationSummary } from '../models/audit-assistant.model';
import { decodeAISearchResults } from './audit-assistant-results.decoder';

/** Incremental SSE decoding; HTTP progress contains the cumulative decoded text. */
export class AIStreamDecoder {
  private consumed = 0;
  private buffer = '';

  read(text: string): AIStreamEvent[] {
    this.buffer += text.slice(this.consumed);
    this.consumed = text.length;
    const events: AIStreamEvent[] = [];
    let separator: RegExpExecArray | null;
    while ((separator = /\r?\n\r?\n/.exec(this.buffer))) {
      const frame = this.buffer.slice(0, separator.index);
      this.buffer = this.buffer.slice(separator.index + separator[0].length);
      const lines = frame.split(/\r?\n/);
      const kind = lines.find(line => line.startsWith('event:'))?.slice(6).trim();
      const data = lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).replace(/^ /, '')).join('\n');
      if (!data) continue; // SSE comments / heartbeat.
      const value = JSON.parse(data);
      if (!value || typeof value !== 'object') throw new Error('Invalid AI stream');
      if (kind === 'status' && typeof value.status === 'string') {
        // Progress metadata may evolve independently of an already-open chat.
        const status = AI_PROGRESS_STATUSES.includes(value.stage) ? value.stage : value.status;
        if (AI_PROGRESS_STATUSES.includes(status)) events.push({ kind, status });
      } else if (kind === 'delta' && typeof value.text === 'string') {
        events.push({ kind, text: value.text });
      } else if (kind === 'results') {
        events.push({ kind, results: decodeAISearchResults(value) });
      } else if (kind === 'checklist_created' && typeof value.checklist_id === 'string' &&
          /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value.checklist_id) &&
          typeof value.title === 'string' && value.title.trim() && value.title.length <= 255 &&
          Number.isSafeInteger(value.version) && value.version >= 1 && value.status === 'draft') {
        events.push({ kind, checklist: value });
      } else if (kind === 'done' && typeof value.text === 'string' && typeof value.refused === 'boolean' &&
          (value.status === 'completed' || value.status === 'incomplete')) {
        events.push({ kind, text: value.text, status: value.status, refused: value.refused });
      } else if (kind === 'error' && typeof value.code === 'string' && typeof value.detail === 'string') {
        events.push({ kind, code: value.code, detail: value.detail });
      } else {
        throw new Error('Invalid AI stream');
      }
    }
    return events;
  }
}

@Injectable({ providedIn: 'root' })
export class AuditAssistantApi {
  private readonly http = inject(HttpClient);

  conversations(exclude?: string): Observable<{ results: AIConversationSummary[] }> {
    return this.http.get<{ results: AIConversationSummary[] }>('/api/juno-ai/api/audits/chat/conversations/',
      { params: exclude ? { exclude } : {} });
  }

  history(conversationId: string, before?: number): Observable<AIConversationHistory> {
    return this.http.get<AIConversationHistory>(
      `/api/juno-ai/api/audits/chat/conversations/${encodeURIComponent(conversationId)}/`,
      { params: before === undefined ? {} : { before } });
  }

  stream(message: string, language: AILanguage, conversationId?: string): Observable<AIStreamEvent> {
    return new Observable(observer => {
      const decoder = new AIStreamDecoder();
      let terminal = false;
      let receivedConversation = false;
      const subscription = this.http.post('/api/juno-ai/api/audits/chat/stream/', {
        message, language, result_format: 'structured',
        ...(conversationId ? { conversation_id: conversationId } : {}),
      }, {
        observe: 'events', responseType: 'text', reportProgress: true,
        headers: { Accept: 'text/event-stream' },
      }).subscribe({
        next: event => {
          if (!receivedConversation && (event.type === HttpEventType.ResponseHeader || event.type === HttpEventType.Response)) {
            const id = event.headers.get('X-Conversation-ID');
            if (id && /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id)) {
              receivedConversation = true;
              observer.next({ kind: 'conversation', conversationId: id });
            }
          }
          const text = event.type === HttpEventType.DownloadProgress ? event.partialText :
            event.type === HttpEventType.Response ? event.body : undefined;
          if (text == null || terminal) return;
          try {
            for (const item of decoder.read(text)) {
              observer.next(item);
              if (item.kind === 'done' || item.kind === 'error') {
                terminal = true;
                observer.complete();
                break;
              }
            }
          } catch {
            observer.error(new Error('Invalid AI stream'));
          }
        },
        error: error => observer.error(error),
        complete: () => {
          if (!terminal) observer.error(new Error('AI stream interrupted'));
        },
      });
      // Unsubscribing aborts the HTTP request, preserving the normal auth interceptor.
      return () => subscription.unsubscribe();
    });
  }
}
